import { CheckBoxIcon, MotionPlayIcon, SyncDesktopIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { WorkItemStatuses } from '@/types'
import { ChevronDown, Trash2, Upload, X } from 'lucide-react'

const statusTestIdSlug = (status: string) => status.toLowerCase().replace(/\s+/g, '-')

interface BacklogSelectionBarProps {
  selectedCount: number
  onTrigger?: () => void
  triggerDisabled?: boolean
  triggerDisabledReason?: string
  onPush?: () => void
  onCancel: () => void
  pushDisabled?: boolean
  pushDisabledReason?: string
  onChangeStatus?: (status: string) => void
  canCompleteWorkItems?: boolean
  statusChangeDisabled?: boolean
  onDelete?: () => void
  onExport: () => void
}

export function BacklogSelectionBar({
  selectedCount,
  onTrigger,
  triggerDisabled,
  triggerDisabledReason,
  onPush,
  onCancel,
  pushDisabled,
  pushDisabledReason,
  onChangeStatus,
  canCompleteWorkItems,
  statusChangeDisabled,
  onDelete,
  onExport,
}: BacklogSelectionBarProps) {
  const statusOptions = Object.values(WorkItemStatuses).filter(
    (status) => status !== WorkItemStatuses.Completed || canCompleteWorkItems,
  )

  if (selectedCount === 0) return null

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 whitespace-nowrap rounded-lg border border-border-accent bg-background px-4 py-2 shadow-lg"
      role="region"
      aria-label="Selection actions"
      data-testid="backlog-selection-bar"
    >
      <div className="flex shrink-0 items-center gap-2 pr-3 border-r">
        <CheckBoxIcon className="h-4 w-4 text-primary" />
        <span className="shrink-0 text-sm font-medium" data-testid="backlog-selection-count">
          {selectedCount} selected
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="gap-2"
        onClick={onExport}
        data-testid="backlog-selection-export"
      >
        <Upload className="h-4 w-4" />
        Export CSV
      </Button>
      {onTrigger && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={onTrigger}
                  disabled={triggerDisabled}
                  data-testid="backlog-bulk-trigger"
                >
                  <MotionPlayIcon className="h-4 w-4" />
                  Trigger
                </Button>
              </span>
            </TooltipTrigger>
            {triggerDisabled && triggerDisabledReason && (
              <TooltipContent>{triggerDisabledReason}</TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )}
      {onPush && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-2"
          onClick={onPush}
          disabled={pushDisabled}
          title={pushDisabled ? pushDisabledReason : undefined}
          data-testid="backlog-bulk-push"
        >
          <SyncDesktopIcon className="h-4 w-4" />
          Push
        </Button>
      )}
      {onChangeStatus && (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-[124px] justify-between gap-2 rounded-lg border border-border bg-background font-normal"
              disabled={statusChangeDisabled}
              data-testid="backlog-bulk-status"
            >
              Status
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="start"
            sideOffset={8}
            className="border-none shadow-elevated rounded-lg py-2 px-0 bg-background gap-0.5 min-w-[180px]"
            data-testid="backlog-bulk-status-menu"
          >
            {statusOptions.map((status) => (
              <DropdownMenuItem
                key={status}
                onClick={() => onChangeStatus(status)}
                className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-normal leading-[1.6] tracking-[0.015em]"
                data-testid={`backlog-bulk-status-option-${statusTestIdSlug(status)}`}
              >
                {status}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {onDelete && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-2 text-danger-foreground hover:text-danger-foreground hover:bg-danger"
          onClick={onDelete}
          data-testid="backlog-bulk-delete"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="gap-2"
        onClick={onCancel}
        data-testid="backlog-selection-cancel"
      >
        <X className="h-4 w-4" />
        Cancel
      </Button>
    </div>
  )
}
